import { lazy } from 'react';
import type { ModuleManifest } from '../../lib/host/types';
import { inspectionTypesKeys } from './queries/keys';

// Inspection Types module (P03 §5, §7.2; story 3-3b) — the Settings surface for
// the procedure catalogue 3-3a's contract created.
//
// WHY A MODULE, and what that buys (P18 §1, DOD §1.4/§1.8). The surface attaches
// through the module host: the manifest below contributes the Settings row and
// the route, so `src/App.tsx` and `src/lib/registries/index.ts` are NOT edited by
// this story — "registries, not shell edits" is satisfied structurally rather
// than by convention. `useModuleRoutes` (lib/host/ModuleRoutes.tsx) already
// renders `<RouteDenied reason="permission"/>` for a hard/bookmarked URL when the
// route's `permission` is missing, which is 2-104's route-containment rule
// applied to module routes.
//
// WHAT IS DELIBERATELY *NOT* IN THIS FOLDER: the TypePicker. It is consumed by
// the create-inspection dialog, which is shell code — and the shell may not
// import a module (that is the pluggability test, and the eslint
// `no-restricted-imports` rule). So the picker lives in
// `src/components/inspections/TypePicker.tsx` and the copy both surfaces share
// lives in `src/lib/inspectionTypes.ts`. Deleting this folder still leaves the
// app compiling, and still leaves the picker working.
//
// `featureKeys: ['inspections.core']` — an inspection type IS the procedure an
// inspection follows, so it lives and dies with the inspections feature. It is
// GA and `defaultForNewOrgs: true` in the backend catalog, so this row is
// permission-gated in practice, never plan-locked; inventing a new flag key here
// would advertise an upsell that does not exist.
//
// No `entities` are declared, so G-21 (entity → searchRegistry) does not apply:
// a Type is org CONFIGURATION reached from Settings, not a record a user hunts
// for in Cmd+K. When Types become searchable that is a story that adds
// `entities` + `search` together, which the manifest union enforces.
export const manifest: ModuleManifest = {
  key: 'inspectionTypes',
  featureKeys: ['inspections.core'],
  settings: [
    {
      key: 'inspection-types',
      slot: 'settings',
      label: 'Inspection types',
      description:
        'The procedures your team can run: which templates apply, which roles may perform them, the severity scale and the SLA.',
      to: '/app/settings/inspection-types',
      // types:read, not types:manage. Every staff role holds `types:read`
      // (P01 systemRoles) and a manager/inspector genuinely needs to SEE the
      // catalogue; editing one edits `allowedRoleIds`, i.e. authorization, so
      // the mutating controls inside the page gate separately on `types:manage`.
      // Gating the ROW on manage would hide the read-only view from everyone who
      // is supposed to have it.
      permission: 'types:read',
      order: 30,
    },
  ],
  routes: [
    {
      path: '/app/settings/inspection-types',
      element: lazy(() =>
        import('./pages/InspectionTypesPage').then((m) => ({ default: m.InspectionTypesPage })),
      ),
      chrome: 'shell',
      // 2-147: collection family — already on EntityListPage + useCursorList
      // + useUrlState, same as /app/review, /app/reports. Prior allowlist
      // entry (2-146d) was stale; see story evidence for the full trace.
      layoutFamily: 'collection',
      // Same gate as the row above (story 2-20's route ↔ row alignment).
      permission: 'types:read',
    },
  ],
  // P09 §2.1 parity. These capabilities SHIPPED WITH THE ENDPOINTS in story
  // 3-3a (agents/src/capabilities/inspectionTypes.js) — this story adds no
  // endpoint, so it adds no capability; naming them here is the module
  // declaring which gateway tools belong to it, which is what the host reads.
  agentTools: [
    'inspection_types_list',
    'inspection_type_get',
    'inspection_type_validation',
    'inspection_type_create',
    'inspection_type_update',
  ],
  queryKeys: inspectionTypesKeys,
};
